import { useEffect, useState } from "react";
import { Loader2, Save, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const PAGES = [
  { key: "home", label: "Home", path: "/docstate" },
  { key: "buy", label: "Buy", path: "/docstate/buy" },
  { key: "rent", label: "Rent", path: "/docstate/rent" },
  { key: "offplan", label: "Off-Plan", path: "/docstate/off-plan" },
  { key: "commercial", label: "Commercial", path: "/docstate/commercial" },
  { key: "about", label: "About", path: "/docstate/about" },
  { key: "insights", label: "Insights", path: "/docstate/insights" },
  { key: "contact", label: "Contact", path: "/docstate/contact" },
];

const titleTone = (n) => (n === 0 ? "text-[#91837C]" : n > 60 ? "text-red-500" : n >= 50 ? "text-emerald-600" : "text-amber-600");
const descTone = (n) => (n === 0 ? "bg-[#E8E2D9]" : n > 160 ? "bg-red-500" : n >= 145 ? "bg-emerald-500" : "bg-amber-500");

export const ReSeoTab = () => {
  const [settings, setSettings] = useState(null);
  const [pages, setPages] = useState({});
  const [tracking, setTracking] = useState({ ga4_id: "", gtm_id: "", meta_pixel_id: "", gsc_verification: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/realestate/settings").then((r) => {
      const s = r.data || {};
      setSettings(s);
      setPages(s.seo_pages || {});
      setTracking({
        ga4_id: s.ga4_id || "", gtm_id: s.gtm_id || "",
        meta_pixel_id: s.meta_pixel_id || "", gsc_verification: s.gsc_verification || "",
      });
    }).catch(() => setSettings({}));
  }, []);

  const setField = (key, field, value) => setPages((p) => ({ ...p, [key]: { ...(p[key] || {}), [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/realestate/settings", {
        ...settings,
        seo_pages: pages,
        ga4_id: tracking.ga4_id.trim(),
        gtm_id: tracking.gtm_id.trim(),
        meta_pixel_id: tracking.meta_pixel_id.trim(),
        gsc_verification: tracking.gsc_verification.trim(),
      });
      toast.success("SEO settings saved");
    } catch { toast.error("Could not save SEO settings"); }
    finally { setSaving(false); }
  };

  if (settings === null) return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#C9A24B]" /></div>;

  const base = (settings.site_url || "docstateproperties.ae").replace(/^https?:\/\//, "").replace(/\/$/, "");

  return (
    <div className="space-y-8" data-testid="re-seo-tab">
      <div className="glass rounded-3xl p-7">
        <h2 className="font-serif text-xl font-semibold text-[#1A1615]">Page Metadata</h2>
        <p className="mt-1 text-xs font-light text-[#91837C]">Titles & descriptions shown in Google. Aim for 50–60 char titles and 145–160 char descriptions.</p>
        <div className="mt-6 space-y-7">
          {PAGES.map((p) => {
            const v = pages[p.key] || {};
            const title = v.title || "";
            const desc = v.description || "";
            return (
              <div key={p.key} className="rounded-2xl border border-[#EBE3DA] bg-white/60 p-5" data-testid={`re-seo-page-${p.key}`}>
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#B38B2D]">{p.label} <span className="text-[#91837C]">· {p.path}</span></p>
                <div className="mt-3">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-[#5A504B]">SEO Title</label>
                    <span className={`text-[11px] font-semibold tabular-nums ${titleTone(title.length)}`}>{title.length}/60</span>
                  </div>
                  <input data-testid={`re-seo-title-${p.key}`} className="input-lux mt-1.5 text-sm" value={title} onChange={(e) => setField(p.key, "title", e.target.value)} />
                </div>
                <div className="mt-4">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-[#5A504B]">Meta Description</label>
                    <span className="text-[11px] font-semibold tabular-nums text-[#91837C]">{desc.length}/160</span>
                  </div>
                  <textarea data-testid={`re-seo-desc-${p.key}`} className="input-lux mt-1.5 min-h-[64px] text-sm" value={desc} onChange={(e) => setField(p.key, "description", e.target.value)} />
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#F0EAE2]">
                    <div className={`h-full rounded-full transition-all duration-300 ${descTone(desc.length)}`} style={{ width: `${Math.min(100, (desc.length / 160) * 100)}%` }} />
                  </div>
                </div>
                <div className="mt-4 rounded-lg border border-[#EBE3DA] bg-white p-4">
                  <p className="mb-1.5 flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-[0.2em] text-[#91837C]"><Search className="h-3 w-3" /> Google preview</p>
                  <p className="truncate text-[15px] leading-snug text-[#1a0dab]">{title || "Page title"}</p>
                  <p className="mt-0.5 text-xs text-[#006621]">{base}{p.path}</p>
                  <p className="mt-0.5 line-clamp-2 text-[13px] leading-snug text-[#545454]">{desc || "Meta description"}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="glass rounded-3xl p-7">
        <h2 className="font-serif text-xl font-semibold text-[#1A1615]">Tracking & Verification</h2>
        <p className="mt-1 text-xs font-light text-[#91837C]">Analytics and search-engine verification for the Docstate portal.</p>
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          {[
            ["ga4_id", "Google Analytics 4 ID", "G-XXXXXXXXXX"],
            ["gtm_id", "Google Tag Manager ID", "GTM-XXXXXXX"],
            ["meta_pixel_id", "Meta (Facebook) Pixel ID", "16-digit pixel ID"],
            ["gsc_verification", "Search Console Verification Token", "HTML tag content value"],
          ].map(([k, label, ph]) => (
            <div key={k}>
              <label className="text-xs font-medium text-[#5A504B]">{label}</label>
              <input data-testid={`re-seo-${k}`} className="input-lux mt-1.5 text-sm" placeholder={ph} value={tracking[k]} onChange={(e) => setTracking((t) => ({ ...t, [k]: e.target.value }))} />
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} data-testid="re-seo-save" className="btn-gold inline-flex items-center gap-2 px-8 py-3 text-xs disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save SEO Settings
        </button>
      </div>
    </div>
  );
};
